import React from 'react'
import styled from 'styled-components'

type AlertType = 'success' | 'warning' | 'error'

interface AlertProps {
  title?: string
  message: string | React.ReactNode
  btnName: string
  type: AlertType
  onClose: () => void
}

const DimBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
`

const AlertWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  width: 450px;
  min-height: 200px;
  max-height: 500px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  text-align: center;
  display: flex;
  flex-direction: column;
  padding: 20px;
  // overflow: hidden;
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px auto;
`

const Title = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
  white-space: pre-wrap;
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ccc;
  margin: 10px 0;
`

const MessageWrapper = styled.div`
  flex-grow: 1;
  overflow: auto; // 스크롤 가능하도록
  max-height: 300px; // 최대 높이 설정
  padding-bottom: 10px; // 여백 추가
`

const Message = styled.div`
  font-size: 14px;
  color: #666;
  flex-grow: 1;
  overflow-y: auto;
  white-space: pre-wrap;
  text-align: center;
  padding: 10px;
`

const CloseButton = styled.button<{ btnType: AlertType }>`
  background-color: ${(props) =>
    props.btnType === 'success'
      ? '#28a745'
      : props.btnType === 'warning'
        ? '#ffc107'
        : '#dc3545'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  margin-top: auto;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      props.btnType === 'success'
        ? '#218838'
        : props.btnType === 'warning'
          ? '#e0a800'
          : '#c82333'};
  }
`

// ✅ SVG 아이콘 추가
const renderIcon = (type: AlertType) => {
  switch (type) {
    case 'success':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#28a745"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="50"
          height="50"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#28a745"
            strokeWidth="3"
            fill="none"
          />
          <path d="M16 8L10 14L7 11" stroke="#28a745" strokeWidth="3" />
        </svg>
      )
    case 'warning':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ffc107"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="50"
          height="50"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#ffc107"
            strokeWidth="3"
            fill="none"
          />
          <line
            x1="12"
            y1="8"
            x2="12"
            y2="14"
            stroke="#ffc107"
            strokeWidth="3"
          />
          <circle cx="12" cy="17" r="1.5" fill="#ffc107" />
        </svg>
      )
    case 'error':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#dc3545"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="50"
          height="50"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#dc3545"
            strokeWidth="3"
            fill="none"
          />
          <line
            x1="8"
            y1="8"
            x2="16"
            y2="16"
            stroke="#dc3545"
            strokeWidth="3"
          />
          <line
            x1="8"
            y1="16"
            x2="16"
            y2="8"
            stroke="#dc3545"
            strokeWidth="3"
          />
        </svg>
      )
    default:
      return null
  }
}

const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  btnName,
  onClose,
}) => {
  return (
    <>
      <DimBackground />
      <AlertWrapper>
        <IconWrapper>{renderIcon(type)}</IconWrapper>
        {title && <Title>{title}</Title>}
        {title && message && <Separator />}
        {message && (
          <MessageWrapper>
            <Message>{message}</Message>
          </MessageWrapper>
        )}
        <CloseButton btnType={type} onClick={onClose}>
          {btnName}
        </CloseButton>
      </AlertWrapper>
    </>
  )
}

export default Alert
